import { useEffect, useMemo, useRef, useState } from "react";
import { ImagePlus, Loader2, UploadCloud, WandSparkles } from "lucide-react";
import AiPreviewLoading from "./AiPreviewLoading";
import AiPreviewResult from "./AiPreviewResult";
import { useLanguage } from "../context/LanguageContext";
import {
  clearAiPreviewOriginalPhoto,
  getAiPreviewSessionId,
  saveAiPreviewOriginalPhoto,
} from "../lib/aiPreviewPhotoStore";

import { preparePhoto, photoMessage, responseError, PHOTO_ACCEPT, checkPreviewImage } from '../lib/photoUpload';

function formatFileSize(size) { return (size / (1024 * 1024)).toFixed(1) + ' MB'; }

export default function AiPreviewUploader() {
  const { t, language } = useLanguage();
  const selection = useRef(0);
  const resultRegion = useRef(null);
  const [isPreparing, setIsPreparing] = useState(false);
  const [notice, setNotice] = useState("");
  const [sourceFile, setSourceFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [generatedUrl, setGeneratedUrl] = useState("");
  const [error, setError] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);

  const copy = useMemo(
    () => ({
      missingImage: t("aiPreview.errors.missingImage"),
      generationUnknown: t("aiPreview.errors.generationUnknown"),
    }),
    [t]
  );

  const fileSummary = useMemo(() => {
    if (!sourceFile) return "";
    return `${sourceFile.name} - ${formatFileSize(sourceFile.size)}`;
  }, [sourceFile]);

  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!generatedUrl || !window.matchMedia("(max-width: 1023px)").matches) return;

    const frame = window.requestAnimationFrame(() => {
      const result = resultRegion.current;
      if (!result) return;

      const navbar = document.querySelector('[data-testid="navbar"]');
      const headerOffset = (navbar?.getBoundingClientRect().height || 72) + 16;
      const top = window.scrollY + result.getBoundingClientRect().top - headerOffset;
      const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      result.focus({ preventScroll: true });
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion ? "auto" : "smooth" });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [generatedUrl]);

  const resetResult = () => {
    setGeneratedUrl("");
  };

  const resetPreview = async () => {
    setSourceFile(null);
    setGeneratedUrl("");
    setError("");
    setNotice("");
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl("");
    await clearAiPreviewOriginalPhoto();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    const current = ++selection.current;
    setError('');
    setNotice('');
    resetResult();
    setSourceFile(null);
    setPreviewUrl('');
    setIsPreparing(true);
    try {
      await clearAiPreviewOriginalPhoto();
      const prepared = await preparePhoto(file);
      if (current !== selection.current) return;
      setSourceFile(prepared);
      setPreviewUrl(URL.createObjectURL(prepared));
      const saved = await saveAiPreviewOriginalPhoto(prepared);
      if (!saved && current === selection.current) setNotice(photoMessage(language, 'storage'));
    } catch (err) {
      if (current === selection.current) setError(photoMessage(language, err.message) || photoMessage(language, 'preparation'));
    } finally {
      if (current === selection.current) setIsPreparing(false);
    }
  };

  const generatePreview = async () => {
    if (isGenerating || isPreparing) return;
    if (!sourceFile) { setError(copy.missingImage); return; }

    setIsGenerating(true);
    setError("");

    const formData = new FormData();
    formData.append("image", sourceFile);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 250000);
    try {
      const response = await fetch("/api/ai-preview", {
        method: "POST",
        headers: {
          "X-AI-Preview-Session": getAiPreviewSessionId(),
          "X-Eco-Bain-Language": language,
        },
        body: formData,
        signal: controller.signal,
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data?.image) {
        throw new Error(responseError(response, data, language));
      }

      await checkPreviewImage(data.image).catch(() => { throw new Error(photoMessage(language, 'result')); });
      setGeneratedUrl(data.image);
    } catch (err) {
      setError(err?.name === 'AbortError' ? photoMessage(language, 'timeout') : err instanceof TypeError ? photoMessage(language, 'network') : err?.message || copy.generationUnknown);
    } finally {
      clearTimeout(timer);
      setIsGenerating(false);
    }
  };

  return (
    <div className="grid max-w-full min-w-0 gap-6 overflow-hidden lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:items-start">
      <div className="min-w-0 max-w-full overflow-hidden rounded-2xl border border-slate-200 bg-white/85 p-4 shadow-sm dark:border-white/10 dark:bg-white/5 sm:rounded-3xl sm:p-6">
        <div className="mb-5 flex min-w-0 items-start gap-3">
          <div className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-[#66C95E]/12 text-[#66C95E]">
            <ImagePlus size={23} />
          </div>
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold leading-tight text-slate-900 dark:text-white">
              {t("aiPreview.uploader.title")}
            </h2>
            <p className="mt-1 max-w-full text-sm text-slate-600 dark:text-white/60">
              {t("aiPreview.uploader.hint")}
            </p>
          </div>
        </div>

        <label className="group flex min-h-[190px] max-w-full cursor-pointer flex-col items-center justify-center overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/70 p-4 text-center transition hover:border-[#66C95E]/70 hover:bg-[#66C95E]/5 dark:border-white/15 dark:bg-white/[0.03] sm:min-h-[220px] sm:rounded-3xl sm:p-6">
          <UploadCloud className="mb-4 h-10 w-10 shrink-0 text-[#66C95E]" />
          <span className="max-w-full text-wrap text-base font-semibold leading-snug text-slate-800 dark:text-white">
            {t("aiPreview.uploader.choose")}
          </span>
          <span className="mt-2 max-w-full text-wrap text-sm leading-relaxed text-slate-500 dark:text-white/45">
            {t("aiPreview.uploader.visibleTubHint")}
          </span>
          <input
            type="file"
            accept={PHOTO_ACCEPT}
            disabled={isPreparing || isGenerating}
            className="sr-only"
            onChange={handleFileChange}
          />
        </label>

        {isPreparing && <p role="status" className="mt-3 text-sm">{photoMessage(language, 'preparing')}</p>}
        {notice && <p role="status" className="mt-3 text-sm">{notice}</p>}
        {fileSummary && (
          <p className="mt-3 flex max-w-full min-w-0 gap-1 text-sm text-slate-600 dark:text-white/55">
            <span className="shrink-0">{t("aiPreview.uploader.preparedFile")}</span>
            <span className="min-w-0 truncate font-semibold">{fileSummary}</span>
          </p>
        )}

        {isGenerating && (
          <div
            role="status"
            aria-live="polite"
            aria-atomic="true"
            className="mt-4 flex items-center gap-3 rounded-2xl bg-[#66C95E]/10 px-4 py-3 text-left text-slate-800 dark:text-white/85"
          >
            <Loader2 className="h-5 w-5 shrink-0 animate-spin text-[#66C95E]" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold">{t("aiPreview.uploader.generating")}</p>
              <p className="mt-0.5 text-xs leading-relaxed text-slate-600 dark:text-white/60">
                {t("aiPreview.uploader.generatingHint")}
              </p>
            </div>
          </div>
        )}

        {error && (
          <p role="alert" className="mt-4 max-w-full rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm leading-relaxed text-red-700 dark:border-red-400/20 dark:bg-red-500/10 dark:text-red-200">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={generatePreview}
          disabled={!sourceFile || isGenerating || isPreparing}
          className="mt-5 inline-flex w-full max-w-full items-center justify-center gap-2 rounded-full bg-[#66C95E] px-4 py-3.5 text-center text-base font-semibold leading-snug text-white shadow-sm transition hover:bg-[#58B551] disabled:cursor-not-allowed disabled:opacity-55 sm:px-6"
        >
          {isGenerating ? (
            <Loader2 className="h-5 w-5 shrink-0 animate-spin" aria-hidden="true" />
          ) : (
            <WandSparkles className="h-5 w-5 shrink-0" aria-hidden="true" />
          )}
          <span className="min-w-0">{isGenerating ? t("aiPreview.uploader.generating") : t("aiPreview.uploader.generate")}</span>
        </button>
      </div>

      <div className="min-w-0 max-w-full space-y-5 overflow-hidden">
        {previewUrl && !generatedUrl && !isGenerating && (
          <div className="max-w-full overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-white/10 dark:bg-white/5 sm:rounded-3xl">
            <img src={previewUrl} alt={t("aiPreview.uploader.previewAlt")} className="h-auto max-h-[620px] w-full max-w-full object-contain" />
          </div>
        )}

        {isGenerating && <AiPreviewLoading />}

        {previewUrl && generatedUrl && (
          <div
            ref={resultRegion}
            tabIndex={-1}
            role="region"
            aria-label={t("aiPreview.result.regionLabel")}
            className="scroll-mt-24 rounded-2xl outline-none focus-visible:ring-2 focus-visible:ring-[#66C95E] focus-visible:ring-offset-4 dark:focus-visible:ring-offset-[#070b12] sm:rounded-3xl"
          >
            <AiPreviewResult
              beforeSrc={previewUrl}
              afterSrc={generatedUrl}
              onRetry={resetPreview}
              isGenerating={isGenerating}
            />
          </div>
        )}

        {!previewUrl && !isGenerating && (
          <div className="max-w-full rounded-2xl border border-slate-200 bg-white/70 p-6 text-center shadow-sm dark:border-white/10 dark:bg-white/5 sm:rounded-3xl sm:p-8">
            <p className="text-lg font-semibold text-slate-800 dark:text-white">
              {t("aiPreview.empty.title")}
            </p>
            <p className="mt-2 text-sm text-slate-500 dark:text-white/45">
              {t("aiPreview.empty.text")}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
